import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { withAlert } from "react-alert";
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { withRouter } from "react-router-dom";
import ReactDataGrid from "@inovua/reactdatagrid-enterprise";
import "@inovua/reactdatagrid-community/index.css";
import {
  AddCircleOutline,
  HelpOutlined as HelpIcon,
  PlaylistAddCheck,
  Undo
} from "@material-ui/icons";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import ConceptSelectEditor from "./components/ConceptSelectEditor";
import BackButton from "../../components/BackButton";
import Group from "../../waybee-ui/Group";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import ClassService from "../../services/ClassService";
import { getDataGridI18n } from "../../utils/getDataGridI18n";
import { getDataGridScrollProps } from "../../utils/getDataGridScrollProps";
import HoverPopover from "../../components/HoverPopover";
import BlockButton from "../../waybee-ui/BlockButton/BlockButton";
import HeaderDetails from "./components/HeaderDetails";
import TestDialog from "./components/TestDialog";
import SimpleDialog from "../../waybee-ui/Dialog/SimpleDialog";
import Dialog from "../../waybee-ui/Dialog";
import DialogTitle from "../../waybee-ui/Dialog/DialogTitle";
import Heading from "../../waybee-ui/Heading";
import DialogContent from "../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../waybee-ui/Dialog/DialogActions";
import { AbilityContext, Can } from "../../acl/can";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import { isJobInProgress } from "../../utils/jobHelpers";
import JobInProgressLoading from "../../components/JobInProgressLoading";
import GridFullscreen from "../../components/GridFullscreen";
import { gradeCellBackgroundColor } from "../../demo/gradeCellColor";
import {
  DEMO_COL_P1,
  DEMO_COL_P2,
  DEMO_COL_MEDIA,
  DEMO_COL_MEDIA_GERAL,
  computeDemoMediaFromProvas,
  computeDemoMediaGeralFromRow
} from "../../demo/buildDemoGradesV2";
import {
  buildGradesHighlightLegend,
  normalizeGradesHighlights,
  withGradesCellHighlight
} from "../../utils/gradesGridHighlights";
import ColorLegend from "../../waybee-ui/ColorLegend";
import {
  filterGradesByStagePrefix,
  gridHasEvaluations,
  hidesNewEvaluation,
  isTemplateButtonVisible,
  screenParams
} from "./systemTemplateGrid";

const getRequestErrorMessage = (error, fallback) => {
  const data = error && error.response && error.response.data;
  const candidates = [
    data && data.error && data.error.message,
    data && data.message,
    typeof data === "string" ? data : null,
    error && error.message
  ];

  const cleaned = candidates
    .filter(candidate => typeof candidate === "string")
    .map(candidate =>
      candidate
        .split("=======================================")[0]
        .replace(/^[\s\u2013-]+/, "")
        .trim()
    )
    .find(
      message =>
        message && !/^Request failed with status code \d+/i.test(message)
    );

  return cleaned || fallback;
};

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

const CellText = styled.p`
  color: rgba(0, 0, 0, 0.5);
  font-size: 18px;
  text-overflow: ellipsis;
  overflow: hidden;
  ${({ editable }) =>
    !!editable &&
    css`
      margin: 0;
      border: 1px solid rgba(0, 0, 0, 0.25);
      padding: 5px;
      border-radius: 5px;
      min-height: 30px;
    `}
`;
const RollNum = styled.span`
  color: ${({ theme }) => theme.color.primary.main};
  margin-right: 12px;
`;

const Footer = styled(Grid)`
  background-color: white;
  border-right: 1px solid #e4e3e2;
  border-bottom: 1px solid #e4e3e2;
  border-left: 1px solid #e4e3e2;
  display: flex;
  justify-content: end;
  padding: 10px;
`;
const SaveButton = styled(Button)`
  height: 44px;
  font-size: 14px;
  margin-left: 16px;
`;

const PatternButton = styled(Button)`
  height: 44px;
  font-size: 14px;
`;

const SearchButton = styled(Button)`
  width: 100%;
  height: 44px;
  font-size: 14px;
`;

const HelpButton = styled(Button)`
  padding: 0 64px;
  border-radius: 0;
  color: rgba(0, 0, 0, 0.5);
  border-right: 1px solid #ccc;

  p {
    margin: 0 0 0 8px;
    text-transform: capitalize;
  }
`;

const Header = styled(Grid)`
  background-color: white;
  border-right: 1px solid #e4e3e2;
  border-top: 1px solid #e4e3e2;
  border-left: 1px solid #e4e3e2;
  display: flex;
  justify-content: end;
  padding: 10px;
`;

class ClassGradesV2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rawData: null,
      dataSource: [],
      columns: [],
      groups: [],
      courses: [],
      periods: [],
      testTypes: [],
      complementaryFields: [],
      loadingFilters: false,
      loadingTable: false,
      loadingSaveTest: false,
      loadingSaveGrade: false,
      loadingJobs: [],
      loadingPeriods: false,
      loadingMessage: "",
      currentClassString: "",
      processedJobs: [],
      openTestDialog: false,
      openConfirmTest: false,
      openFormula: false,
      filter: {},
      values: {},
      processError: null,
      evaluation: {},
      isEditing: false,
      gridRef: null,
      cellSelection: {},
      openApplyPattern: false,
      openRevertPattern: false,
      patternConcept: "",
      patternNumeric: "",
      patternSnapshot: null,
      highlights: [],
      templateConfig: {},
      systemTemplateId: null,
      applyingTemplate: false,
      gradesSearched: false,
      gridHasEvaluations: false
    };
  }

  async componentDidMount() {
    this.getCourses();
    this.loadSystemTemplate();
  }

  componentDidUpdate(prevProps) {
    const {
      jobsState: { jobs }
    } = this.props;
    const { filter, loadingJobs, processedJobs } = this.state;
    const currentClass = localStorage.getItem("class");

    if (loadingJobs.length !== 0 && jobs.length >= 1) {
      jobs.forEach((job, index) => {
        const previousJob = prevProps.jobsState.jobs[index];
        const hasProcessed = processedJobs.includes(job.id);

        if (
          !hasProcessed &&
          (job.status === "success" || job.status === "error") &&
          previousJob &&
          job.status !== previousJob.status
        ) {
          const filteredJobs = loadingJobs.filter(
            pendingJob =>
              pendingJob !==
              currentClass + (filter.course && filter.course.id) + filter.period
          );

          if (filteredJobs.length !== loadingJobs.length || !hasProcessed) {
            this.updateJobsProcessedJobs(filteredJobs, job.id);
          }
        }
      });
    }
  }

  getGradePeriods = async idTurmaDisc => {
    const { alert, t } = this.props;

    try {
      const periods = await ClassService.getGradePeriods(idTurmaDisc);
      if (!periods || !periods.length) {
        this.setState({
          periods: [],
          processError: new Error(t("grades.v2.noPeriods"))
        });
        return;
      }

      let currentPeriod = periods.find(period => period.currentPeriod);
      if (!currentPeriod) [currentPeriod] = periods;
      this.setState({ periods });
      this.setFilter("period", currentPeriod.id);
    } catch (e) {
      console.error(e);
      alert.show(t("grades.v2.errorPeriods"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingFilters: false, loadingPeriods: false });
    }
  };

  getCourses = async () => {
    const { match, alert, t } = this.props;

    this.setState({ loadingFilters: true });

    const classId = match.params.id;
    try {
      const disciplines = await ClassService.getDisciplines(classId);
      /* Um único setState + callback: filter.course e períodos ficam coerentes (cf. ReleaseGrades). */
      /* Prefere filha/avulsa — mãe só agrupa (não lança nota) */
      const firstAccessible =
        disciplines && disciplines.length
          ? disciplines.find(
              c => c.teacherAccess !== false && c.mother !== true
            ) ||
            disciplines.find(c => c.teacherAccess !== false) ||
            disciplines[0]
          : null;
      this.setState(
        prev => ({
          courses: disciplines || [],
          filter: firstAccessible
            ? { ...prev.filter, course: firstAccessible }
            : prev.filter
        }),
        () => {
          if (firstAccessible) {
            this.getGradePeriods(firstAccessible.idTurmaDisc);
          } else {
            this.setState({ loadingFilters: false });
          }
        }
      );
    } catch (e) {
      console.error(e);
      alert.show(t("common.messages.errorFetchDisciplines"), {
        title: t("common.actions.error")
      });
      this.setState({ loadingFilters: false });
    }
  };

  getJobQueue = () => {
    const { jobsState, t } = this.props;
    const { filter } = this.state;
    const currentClass = localStorage.getItem("class");

    jobsState.jobs.map(job => {
      if (isJobInProgress(job) && job.classId === currentClass) {
        if (
          filter.course &&
          job.course === filter.course.id &&
          job.period === filter.period
        ) {
          this.setState(prevState => ({
            loadingJobs: [
              ...prevState.loadingJobs,
              job.classId + job.course + job.period
            ],
            loadingMessage: t("grades.v2.jobInProgress", {
              course: job.course,
              period: job.period
            })
          }));
        }
      }
      return null;
    });
  };

  getGrades = async (idTurmaDisc, period) => {
    const { alert, t, location } = this.props;
    const { filter } = this.state;
    const currentClass = localStorage.getItem("class");
    const pendingGradeIdentifier =
      currentClass +
      (filter.course && filter.course.id) +
      (filter.period != null ? filter.period : "");

    this.setState({
      loadingTable: true,
      currentClassString: pendingGradeIdentifier
    });

    this.getJobQueue();

    let grades;
    try {
      grades = await ClassService.getGradesGridData(idTurmaDisc, period);
    } catch (e) {
      console.error(e);
      alert.show(t("grades.v2.errorGrid"), {
        title: t("common.actions.error")
      });
      this.setState({ loadingTable: false });
      return;
    }

    const { stagePrefix } = screenParams(location);
    const scopedByStage =
      stagePrefix != null && String(stagePrefix).trim() !== "";
    if (scopedByStage) {
      grades = filterGradesByStagePrefix(grades, stagePrefix);
    }
    const hasEvaluations = gridHasEvaluations(grades);
    if (scopedByStage && !hasEvaluations) {
      this.setState({
        loadingTable: false,
        rawData: null,
        columns: [],
        dataSource: [],
        groups: [],
        gradesSearched: true,
        gridHasEvaluations: false,
        values: {},
        patternSnapshot: null,
        openRevertPattern: false
      });
      return;
    }

    const { groups, students, columns } = grades || {};
    const highlights = normalizeGradesHighlights(grades && grades.highlights);

    if (!groups || !students || !columns) {
      alert.show(t("grades.v2.noRecords"), {
        title: t("grades.v2.noRecordsTitle")
      });
    }

    const groupTitle = group => group.header || group.name;

    const gradesGroups = (groups || []).map(group => ({
      ...group,
      header: <HeaderDetails title={groupTitle(group)} />
    }));

    // Folga fixa além do texto (padding do header + ícones/borda do DataGrid)
    const HEADER_WIDTH_BUFFER_PX = 64;
    // Fonte compacta do header ~16px bold
    const HEADER_CHAR_PX = 10;
    const COL_MIN_PX = 140;
    const COL_MAX_PX = 360;

    /** Largura aproximada do texto do header (14px bold) + folga fixa. */
    const headerTextWidth = text => {
      const label = String(text || "").trim();
      if (!label) return COL_MIN_PX;
      return Math.ceil(label.length * HEADER_CHAR_PX + HEADER_WIDTH_BUFFER_PX);
    };

    const columnsByGroup = (grades.columns || []).reduce((acc, column) => {
      const key = column.group || "aval";
      if (!acc[key]) acc[key] = [];
      acc[key].push(column);
      return acc;
    }, {});

    const widthByColumnId = {};
    Object.keys(columnsByGroup).forEach(groupKey => {
      const cols = columnsByGroup[groupKey];
      const groupMeta = (groups || []).find(
        g => g.name === groupKey || g.header === groupKey
      );
      const groupLabel = groupMeta ? groupTitle(groupMeta) : groupKey;
      const groupNeeded = headerTextWidth(groupLabel);

      const naturalWidths = cols.map(column => {
        const fromHeader = headerTextWidth(column.header);
        return Math.min(COL_MAX_PX, Math.max(COL_MIN_PX, fromHeader));
      });

      const sum = naturalWidths.reduce((a, b) => a + b, 0);
      // Se o nome do grupo (ex.: "Língua Portuguesa") é mais largo que a soma
      // das colunas-filhas (ex.: só "Média"), redistribui a diferença.
      if (sum < groupNeeded && cols.length) {
        const extra = Math.ceil((groupNeeded - sum) / cols.length);
        for (let i = 0; i < naturalWidths.length; i += 1) {
          naturalWidths[i] = Math.min(COL_MAX_PX, naturalWidths[i] + extra);
        }
      }

      cols.forEach((column, i) => {
        widthByColumnId[column.id] = naturalWidths[i];
      });
    });

    const gradesColumns = (grades.columns || []).map(column => {
      const defaultWidth = widthByColumnId[column.id] || 150;

      const columnOptions = {
        name: column.id,
        header: column.header,
        group: column.group,
        editable: !!column.editable,
        defaultWidth,
        minWidth: Math.min(140, defaultWidth),
        headerAlign: "center",
        textAlign: "center",
        showColumnMenuTool: false,
        render: ({ value }) => {
          const gradeBg =
            column.value != null &&
            !column.concepts &&
            gradeCellBackgroundColor(value, column.value);
          const cellStyle = gradeBg ? { backgroundColor: gradeBg } : undefined;
          if (column.concepts && value != null && value !== "") {
            const v = String(value);
            return (
              <CellText editable={column.editable} style={cellStyle}>
                {column.concepts.map(c => {
                  if (value === c.id || v.substring(0, 1) === c.id) {
                    return c.label;
                  }
                  return null;
                })}
              </CellText>
            );
          }

          return (
            <CellText editable={column.editable} style={cellStyle}>
              {value}
            </CellText>
          );
        },
        renderHeader: ({ value }) => (
          <HeaderDetails
            title={value}
            // Cor da fonte: integração (headerColor / COLOR / color)
            color={column.headerColor || column.COLOR || column.color}
            compact
            onClick={() => this.getEvaluationInfo(column)}
          />
        )
      };

      if (column.concepts) {
        columnOptions.editor = ConceptSelectEditor;
        columnOptions.editorProps = {
          idProperty: "id",
          dataSource: column.concepts,
          collapseOnSelect: true
        };
      }

      return withGradesCellHighlight(columnOptions, column.id, highlights);
    });

    const tableEl = document.querySelector(".grades-table");
    gradesColumns.unshift(
      withGradesCellHighlight(
        {
          name: "studentId",
          header: t("common.labels.ra"),
          editable: false,
          locked: true,
          group: "data",
          showColumnMenuTool: false,
          defaultWidth: tableEl ? tableEl.offsetWidth * 0.15 : 150,
          render: ({ value }) => <CellText>{value}</CellText>,
          renderHeader: ({ value }) => <HeaderDetails title={value} />
        },
        "studentId",
        highlights
      )
    );
    gradesColumns.unshift(
      withGradesCellHighlight(
        {
          name: "name",
          header: t("common.labels.student"),
          editable: false,
          locked: true,
          sortable: true,
          group: "data",
          showColumnMenuLockOptions: false,
          showColumnMenuGroupOptions: false,
          showColumnMenuFilterOptions: false,
          defaultWidth: tableEl ? tableEl.offsetWidth * 0.35 : 350,
          render: ({ value, data }) => (
            <HoverPopover key={data.id} id={data.id} value={value}>
              <CellText>
                <RollNum>{data.rollCallNum}</RollNum>
                {value}
              </CellText>
            </HoverPopover>
          ),
          renderHeader: ({ value }) => <HeaderDetails title={value} />
        },
        "name",
        highlights
      )
    );

    this.setState({
      rawData: grades,
      columns: gradesColumns,
      dataSource: students || [],
      testTypes: grades.testTypes,
      complementaryFields: grades.complementaryFields || [],
      groups: gradesGroups,
      highlights,
      loadingTable: false,
      values: {},
      patternSnapshot: null,
      openRevertPattern: false,
      gradesSearched: true,
      gridHasEvaluations: hasEvaluations
    });
  };

  getEvaluationInfo = async column => {
    const { IDTURMADISC, CODETAPA, CODPROVA, TIPO } = column;

    // Coluna média (etapa TOTVS / SNOTAETAPA) — sem prova editável
    if (TIPO === "M" || String(CODPROVA) === "MED" || !CODPROVA) {
      return;
    }

    this.setState({
      loadingSaveGrade: true,
      isEditing: true
    });
    try {
      const evaluationData = await ClassService.getGradeEvaluation(
        IDTURMADISC,
        CODETAPA,
        CODPROVA
      );
      this.setState({ evaluation: evaluationData });
    } catch (e) {
      console.error(e);
    } finally {
      this.setState({
        loadingSaveGrade: false,
        openTestDialog: true
      });
    }
  };

  setFilter(field, value) {
    const { filter } = this.state;
    this.setState({
      filter: {
        ...filter,
        [field]: value
      }
    });
  }

  applySystemTemplate = async () => {
    const { alert, match, t } = this.props;
    const { filter, periods, systemTemplateId } = this.state;
    const period = (periods || []).find(item => item.id === filter.period);
    if (
      !systemTemplateId ||
      !filter.course ||
      filter.period == null ||
      filter.period === ""
    ) {
      alert.show(t("grades.v2.incompleteFilter"), {
        title: t("common.actions.attention")
      });
      return;
    }
    const currentClass = localStorage.getItem("class");
    this.setState({ applyingTemplate: true });
    try {
      await ClassService.applySystemTemplate(systemTemplateId, {
        classId: match.params.id,
        idTurmaDisc: filter.course.idTurmaDisc,
        courseId: filter.course.id,
        period: filter.period,
        periodName: period ? period.name : ""
      });
      alert.show(t("common.messages.processingRequest"), {
        title: t("grades.v2.saving")
      });
      this.setState(
        prevState => ({
          applyingTemplate: false,
          loadingJobs: [
            ...prevState.loadingJobs,
            currentClass + filter.course.id + filter.period
          ],
          loadingMessage: t("grades.v2.jobInProgress", {
            course: filter.course.id,
            period: filter.period
          })
        }),
        () => {
          fetchGradeJobQueue().then(() => {
            this.getGrades(filter.course.idTurmaDisc, filter.period);
          });
        }
      );
    } catch (e) {
      console.error(e);
      this.setState({ applyingTemplate: false });
      alert.show(getRequestErrorMessage(e, t("grades.v2.templateApplyError")), {
        title: t("common.actions.error")
      });
    }
  };

  loadSystemTemplate = async () => {
    const { location } = this.props;
    const params = screenParams(location);
    if (!Object.keys(params).length) return;
    try {
      const template = await ClassService.getSystemTemplate({
        ...params,
        screen: "gradesV2"
      });
      this.setState({
        systemTemplateId: template && template.id,
        templateConfig: (template && template.config) || {}
      });
    } catch (e) {
      const status = e && e.response && e.response.status;
      if (status !== 404) console.error(e);
    }
  };

  updateJobsProcessedJobs = (filteredLoadingJobs, jobId) => {
    const { filter } = this.state;
    this.setState(
      prev => ({
        loadingJobs: filteredLoadingJobs,
        loadingMessage: "",
        processedJobs: [...prev.processedJobs, jobId]
      }),
      () => {
        if (filter.course && filter.course.idTurmaDisc != null) {
          this.getGrades(filter.course.idTurmaDisc, filter.period);
        }
      }
    );
  };

  handleOnDeleteEvaluation = async () => {
    const { alert, match, t } = this.props;
    const { filter, evaluation } = this.state;
    const {
      IDTURMADISC,
      CODETAPA,
      CODPROVA,
      TIPOETAPA
    } = evaluation.SProvasCompl;

    const data = {
      classId: match.params.id,
      idTurmaDisc: IDTURMADISC,
      codEtapa: CODETAPA,
      codProva: CODPROVA,
      tipoEtapa: TIPOETAPA
    };

    this.setState({
      loadingSaveTest: true,
      openConfirmTest: true,
      openTestDialog: false
    });

    try {
      await ClassService.deleteGradeEvaluations(data);
      if (filter.course && filter.course.idTurmaDisc != null) {
        this.getGrades(filter.course.idTurmaDisc, filter.period);
      }
      alert.show(t("grades.v2.evalChanged"), {
        title: t("common.actions.success")
      });
      this.setState({ loadingSaveTest: false });
    } catch (e) {
      console.error(e);
      alert.show(t("grades.v2.evalChangeError"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({
        openConfirmTest: false,
        loadingSaveTest: false,
        loadingTable: true
      });
    }
  };

  onEditComplete = ({ value, columnId, rowIndex, data }) => {
    const { alert, t } = this.props;
    const { dataSource, values, filter, rawData } = this.state;

    const columns = rawData?.columns;
    if (!Array.isArray(columns)) {
      return false;
    }

    const columnData = columns.find(column => column.id === columnId);

    if (!columnData) {
      return false;
    }

    const normalizeGradeValue = gradeValue => {
      if (gradeValue == null) {
        return "";
      }
      const asString = String(gradeValue).trim();
      if (asString === "" || asString === "undefined" || asString === "null") {
        return "";
      }
      return asString.replace(",", ".");
    };

    const normalizedValue = normalizeGradeValue(value);
    const isInputValid = /^[\d.,]*$/.test(normalizedValue);

    const previousValue =
      dataSource[rowIndex] != null ? dataSource[rowIndex][columnId] : undefined;

    if (normalizeGradeValue(previousValue) === normalizedValue) {
      return true;
    }

    const maxVal = parseFloat(String(columnData.value).replace(",", "."));
    const entered = parseFloat(normalizedValue);

    if (
      normalizedValue !== "" &&
      isInputValid &&
      Number.isFinite(maxVal) &&
      Number.isFinite(entered) &&
      maxVal < entered
    ) {
      alert.show(t("grades.v2.maxExceeded"), {
        title: t("grades.v2.inputError")
      });
      return false;
    }

    const storedData = [...dataSource];
    const nextRow = { ...storedData[rowIndex], [columnId]: normalizedValue };
    if (columnId === DEMO_COL_P1 || columnId === DEMO_COL_P2) {
      nextRow[DEMO_COL_MEDIA] =
        computeDemoMediaFromProvas(
          nextRow[DEMO_COL_P1],
          nextRow[DEMO_COL_P2]
        ) ?? "";
      nextRow[DEMO_COL_MEDIA_GERAL] = computeDemoMediaGeralFromRow(nextRow);
    }
    storedData[rowIndex] = nextRow;

    if (!filter.course || filter.course.idTurmaDisc == null) {
      return false;
    }

    const gradeObj = {
      idExam: columnData.CODPROVA,
      idStep: columnData.CODETAPA,
      typeStep: columnData.TIPOETAPA || columnData.tipoEtapa || "N",
      idTurmaDisc:
        columnData.IDTURMADISC || data.idturmadisc || filter.course.idTurmaDisc,
      idGroup: columnData.TIPO === "C" ? columnData.IDGRUPO : null,
      studentId: data.studentId,
      value: normalizedValue
    };

    this.setState({
      dataSource: storedData,
      values: {
        ...values,
        [`${data.studentId}-${columnId}`]: gradeObj
      }
    });
    /* Inovua: evitar return undefined/null — a grelha pode rejeitar a promise com esse valor. */
    return true;
  };

  changeCourse = course => {
    this.setState({ loadingPeriods: true });
    this.setState(
      prev => ({
        filter: {
          ...prev.filter,
          course: course || null
        }
      }),
      () => {
        const { filter: f } = this.state;
        if (f.course && f.course.idTurmaDisc != null) {
          this.getGradePeriods(f.course.idTurmaDisc);
        } else {
          this.setState({ loadingPeriods: false, periods: [] });
        }
      }
    );
  };

  changePeriod = e => {
    this.setFilter("period", e.target.value);
  };

  getEditableGradeColumns = () => {
    const { rawData } = this.state;
    const columns = rawData?.columns || [];
    return columns.filter(
      col => col.editable && col.CODETAPA != null && col.CODPROVA != null
    );
  };

  getPatternConceptOptions = () => {
    const byId = new Map();
    this.getEditableGradeColumns().forEach(col => {
      (col.concepts || []).forEach(c => {
        if (c && c.id != null && !byId.has(String(c.id))) {
          byId.set(String(c.id), {
            id: String(c.id),
            label: c.label || String(c.id)
          });
        }
      });
    });
    return Array.from(byId.values());
  };

  openApplyPatternDialog = () => {
    const { alert, t } = this.props;
    const conceptOptions = this.getPatternConceptOptions();
    const hasNumericCols = this.getEditableGradeColumns().some(
      c => !c.concepts?.length
    );

    if (!this.getEditableGradeColumns().length) {
      alert.show(t("grades.v2.noEditableCols"), {
        title: t("grades.v2.applyDefault")
      });
      return;
    }

    this.setState({
      openApplyPattern: true,
      patternConcept: conceptOptions[0]?.id || "",
      patternNumeric: hasNumericCols ? "" : ""
    });
  };

  applyPattern = () => {
    const { alert, t } = this.props;
    const {
      dataSource,
      values,
      filter,
      patternConcept,
      patternNumeric,
      rawData,
      patternSnapshot
    } = this.state;

    const editableColumns = this.getEditableGradeColumns();
    if (!editableColumns.length || !dataSource?.length) {
      alert.show(t("grades.v2.noDataDefault"), {
        title: t("grades.v2.applyDefault")
      });
      return;
    }

    if (!filter.course || filter.course.idTurmaDisc == null) {
      alert.show(t("grades.v2.selectSubjectApply"), {
        title: t("grades.v2.applyDefault")
      });
      return;
    }

    const hasConceptCols = editableColumns.some(c => c.concepts?.length);
    const hasNumericCols = editableColumns.some(c => !c.concepts?.length);

    if (hasConceptCols && !patternConcept) {
      alert.show(t("grades.v2.selectConcept"), {
        title: t("grades.v2.applyDefault")
      });
      return;
    }

    if (hasNumericCols) {
      const numeric = String(patternNumeric ?? "").trim();
      if (numeric === "" || !/^[\d.,]+$/.test(numeric)) {
        alert.show(t("grades.v2.invalidDefaultGrade"), {
          title: t("grades.v2.applyDefault")
        });
        return;
      }
    }

    const nextValues = { ...values };
    const nextDataSource = dataSource.map(row => {
      const nextRow = { ...row };
      editableColumns.forEach(column => {
        const isConcept = !!(column.concepts && column.concepts.length);
        const value = isConcept
          ? patternConcept
          : String(patternNumeric).replace(",", ".");

        if (isConcept) {
          const allowed = column.concepts.some(
            c => String(c.id) === String(value)
          );
          if (!allowed) return;
        } else {
          const maxVal = parseFloat(
            String(column.value ?? "").replace(",", ".")
          );
          const entered = parseFloat(String(value).replace(",", "."));
          if (
            Number.isFinite(maxVal) &&
            Number.isFinite(entered) &&
            entered > maxVal
          ) {
            return;
          }
        }

        nextRow[column.id] = value;
        nextValues[`${row.studentId}-${column.id}`] = {
          idExam: column.CODPROVA,
          idStep: column.CODETAPA,
          typeStep: column.TIPOETAPA || column.tipoEtapa || "N",
          idTurmaDisc:
            column.IDTURMADISC || row.idturmadisc || filter.course.idTurmaDisc,
          idGroup: column.TIPO === "C" ? column.IDGRUPO : null,
          studentId: row.studentId,
          value: String(value ?? "").trim()
        };
      });
      return nextRow;
    });

    const snapshot = patternSnapshot || {
      dataSource: JSON.parse(JSON.stringify(dataSource)),
      values: JSON.parse(JSON.stringify(values))
    };

    this.setState({
      dataSource: nextDataSource,
      values: nextValues,
      openApplyPattern: false,
      patternSnapshot: snapshot,
      rawData
    });

    alert.show(t("grades.v2.defaultAppliedMsg"), {
      title: t("grades.v2.defaultApplied")
    });
  };

  revertPattern = () => {
    const { alert, t } = this.props;
    const { patternSnapshot } = this.state;

    if (!patternSnapshot) {
      this.setState({ openRevertPattern: false });
      return;
    }

    this.setState({
      dataSource: JSON.parse(JSON.stringify(patternSnapshot.dataSource)),
      values: JSON.parse(JSON.stringify(patternSnapshot.values)),
      patternSnapshot: null,
      openRevertPattern: false
    });

    alert.show(t("grades.v2.defaultRevertedMsg"), {
      title: t("grades.v2.defaultReverted")
    });
  };

  searchGradesFilter = async () => {
    const { alert, t } = this.props;
    const { filter } = this.state;

    this.setState({ processError: null });
    if (!filter.course || filter.course.idTurmaDisc == null) {
      alert.show(t("grades.v2.selectBeforeSearch"), {
        title: t("grades.v2.incompleteFilter")
      });
      return;
    }
    this.getGrades(filter.course.idTurmaDisc, filter.period);
  };

  saveGrades = async () => {
    const { alert, match, t } = this.props;
    const { values, filter } = this.state;
    const currentClass = localStorage.getItem("class");
    const grades = Object.values(values);
    const classId = match.params.id;

    if (!filter.course || filter.course.idTurmaDisc == null) {
      return alert.show(t("grades.v2.selectBeforeSave"), {
        title: t("grades.v2.incompleteFilter")
      });
    }

    if (!grades.length) {
      return alert.show(t("grades.v2.noChanges"), {
        title: t("grades.v2.noChangesTitle")
      });
    }

    this.setState({ loadingSaveGrade: true });

    try {
      await ClassService.saveGradesV2({ filter, grades, classId });
      alert.show(t("common.messages.processingRequest"), {
        title: t("grades.v2.gradesSent")
      });
      this.setState(
        prevState => ({
          loadingJobs: [
            ...prevState.loadingJobs,
            currentClass + filter.course.id + filter.period
          ],
          loadingMessage: t("grades.v2.jobInProgress", {
            course: filter.course.id,
            period: filter.period
          }),
          values: {},
          patternSnapshot: null,
          openRevertPattern: false
        }),
        () => {
          fetchGradeJobQueue().then(() => {
            this.getGrades(filter.course.idTurmaDisc, filter.period);
          });
        }
      );
    } catch (e) {
      console.error(e);
      const errMsg =
        e && typeof e.message === "string" ? e.message : String(e || "");
      if (!errMsg.includes("timeout")) {
        alert.show(t("grades.v2.saveError", { err: errMsg }), {
          title: t("common.actions.error")
        });
      }
    } finally {
      this.setState({ loadingSaveGrade: false });
    }

    return null;
  };

  saveTest = async data => {
    const { alert, match, t } = this.props;
    const { filter } = this.state;

    this.setState({
      loadingSaveTest: true,
      openConfirmTest: true
    });

    try {
      if (!filter.course || filter.course.idTurmaDisc == null) {
        throw new Error(t("grades.v2.noSubject"));
      }
      await ClassService.saveTest({
        classId: match.params.id,
        idTurmaDisc: filter.course.idTurmaDisc,
        period: filter.period,
        fields: data
      });
      this.setState({
        loadingSaveTest: false,
        openTestDialog: false
      });
      this.getGrades(filter.course.idTurmaDisc, filter.period);
    } catch (e) {
      console.error(e);
      const message = getRequestErrorMessage(e, t("grades.v2.saveEvalError"));
      this.setState({ loadingSaveTest: false, openConfirmTest: false });
      alert.show(message, { title: t("grades.v2.saveEvalErrorTitle") });
    }
  };

  onConceptCellClick = (event, cellProps) => {
    if (!cellProps || cellProps.inEdit || cellProps.headerCell) {
      return;
    }
    const canEdit =
      cellProps.computedEditable != null
        ? cellProps.computedEditable
        : cellProps.editable;
    if (!canEdit || cellProps.editor !== ConceptSelectEditor) {
      return;
    }
    const { gridRef } = this.state;
    if (!gridRef || !gridRef.current) {
      return;
    }
    gridRef.current.startEdit({
      columnId: cellProps.id || cellProps.name,
      rowIndex: cellProps.rowIndex
    });
  };

  scrollTo = index => {
    const { gridRef, columns } = this.state;
    const columnId = parseInt(Object.keys(index)[0].split(",")[1], 10);
    const scrollTo = columnId + 4;

    if (scrollTo <= columns.length) {
      gridRef.current.scrollToColumn(scrollTo, { duration: 100 });
    }
  };

  render() {
    const { match, t } = this.props;
    const classId = match.params.id;
    const {
      columns,
      groups,
      dataSource,
      courses,
      testTypes,
      complementaryFields,
      loadingFilters,
      loadingTable,
      loadingSaveTest,
      loadingSaveGrade,
      loadingJobs,
      loadingPeriods,
      loadingMessage,
      currentClassString,
      openTestDialog,
      openConfirmTest,
      openFormula,
      openApplyPattern,
      openRevertPattern,
      patternConcept,
      patternNumeric,
      patternSnapshot,
      filter,
      periods,
      templateConfig,
      applyingTemplate,
      gradesSearched,
      gridHasEvaluations: hasEvaluations,
      processError,
      isEditing,
      evaluation,
      cellSelection,
      rawData,
      values,
      highlights
    } = this.state;

    const hasGradeChanges = Object.keys(values || {}).length > 0;
    const colorLegendItems = buildGradesHighlightLegend(
      highlights,
      dataSource
    ).map(item => ({
      id: item.id,
      color: item.color,
      label: t(`grades.v2.highlightLegend.${item.kind}`, item.kind)
    }));

    const patternConceptOptions = this.getPatternConceptOptions();
    const patternHasConcepts = this.getEditableGradeColumns().some(
      c => c.concepts?.length
    );
    const patternHasNumeric = this.getEditableGradeColumns().some(
      c => !c.concepts?.length
    );

    const formula = periods.find(period => period.id === filter.period)
      ?.formula;

    const menu = menuProps => {
      menuProps.items.splice(-2, 2);
    };

    const isGradesAvailable =
      loadingJobs.length >= 0 &&
      loadingJobs.some(job => job === currentClassString);
    const isFilterAvailable =
      !isGradesAvailable &&
      (loadingTable || loadingSaveTest || loadingSaveGrade);
    const fallbackMessageOnFirstLoad = t("grades.v2.emptyHint");

    const campusAbility = this.context;
    const showNewEvaluation = !hidesNewEvaluation(templateConfig);
    const templateButtons = (templateConfig.buttons || []).filter(button =>
      isTemplateButtonVisible(button, {
        searched: gradesSearched,
        hasEvaluations
      })
    );
    return (
      <>
        <Grid container spacing={2}>
          <BackButton to={`/classes/${classId}`} />
          {showNewEvaluation && (
            <Can I="create" a="scoresTest">
              {!loadingFilters && !filter?.course?.mother && (
                <Grid item xs={12}>
                  <BlockButton
                    startIcon={<AddCircleOutline />}
                    onClick={() =>
                      this.setState({ openTestDialog: true, isEditing: false })}
                  >
                    {t("grades.v2.addEval")}
                  </BlockButton>
                </Grid>
              )}
            </Can>
          )}
          {templateButtons.map(button => (
            <Grid item xs={12} key={button.title || JSON.stringify(button)}>
              <BlockButton
                disabled={applyingTemplate || !filter.period}
                onClick={this.applySystemTemplate}
              >
                {button.title}
              </BlockButton>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Group fullWidth>
              {loadingFilters ? (
                <LoadContainer>
                  <CircularProgress />
                </LoadContainer>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <SearchableSelect
                      label={t("common.labels.discipline")}
                      options={courses}
                      value={filter.course || null}
                      onChange={this.changeCourse}
                      disabled={isFilterAvailable}
                      disableClearable
                      getOptionLabel={o => o.name || ""}
                      getOptionSelected={(a, b) =>
                        String(a.idTurmaDisc) === String(b.idTurmaDisc)}
                      getOptionDisabled={course =>
                        course.teacherAccess === false || course.mother === true}
                      noOptionsText={t("common.messages.noDiscipline")}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel id="filter-course-label">
                        {t("common.labels.period")}
                      </InputLabel>
                      <Select
                        fullWidth
                        onChange={this.changePeriod}
                        value={filter.period}
                        disabled={isFilterAvailable || loadingPeriods}
                      >
                        {periods.map(period => (
                          <MenuItem value={period.id}>{period.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <SearchButton
                      variant="contained"
                      color="primary"
                      onClick={this.searchGradesFilter}
                      disabled={isFilterAvailable || loadingPeriods}
                    >
                      {t("common.actions.search")}
                    </SearchButton>
                  </Grid>
                </Grid>
              )}
            </Group>
          </Grid>

          {!loadingFilters && processError && (
            <Grid item xs={12}>
              <Group fullWidth>{processError.message}</Group>
            </Grid>
          )}

          {!loadingFilters && !processError && (
            <Grid item xs={12}>
              {loadingTable || isGradesAvailable ? (
                <JobInProgressLoading
                  inProgress={isGradesAvailable}
                  message={loadingMessage}
                />
              ) : (
                <>
                  {!rawData ? (
                    <LoadContainer>
                      <Typography
                        color="textSecondary"
                        variant="h4"
                        style={{ padding: "15px" }}
                      >
                        {fallbackMessageOnFirstLoad}
                      </Typography>
                    </LoadContainer>
                  ) : (
                    <>
                      <ColorLegend
                        title={t("common.colorLegend")}
                        items={colorLegendItems}
                      />
                      <GridFullscreen
                        minHeight={550}
                        renderToolbar={trigger => (
                          <Header
                            item
                            xs={12}
                            style={{ alignItems: "center", gap: 12 }}
                          >
                            {trigger}
                            <Can I="save" a="scoresV2">
                              {patternSnapshot && (
                                <PatternButton
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<Undo />}
                                  onClick={() =>
                                    this.setState({
                                      openRevertPattern: true
                                    })}
                                  style={{ marginRight: 16 }}
                                >
                                  {t("grades.v2.revertDefault")}
                                </PatternButton>
                              )}
                              <PatternButton
                                variant="outlined"
                                color="primary"
                                startIcon={<PlaylistAddCheck />}
                                onClick={this.openApplyPatternDialog}
                              >
                                {t("grades.v2.applyDefault")}
                              </PatternButton>
                              <SaveButton
                                variant="contained"
                                color="primary"
                                disabled={!hasGradeChanges}
                                onClick={this.saveGrades}
                              >
                                {t("common.actions.saveChanges")}
                              </SaveButton>
                            </Can>
                          </Header>
                        )}
                      >
                        {({ isFullscreen }) => (
                          <ReactDataGrid
                            licenseKey={
                              process.env.REACT_APP_DATA_GRID_LICENSE_KEY
                            }
                            className="grades-table"
                            idProperty="studentId"
                            onReady={e => this.setState({ gridRef: e })}
                            columns={columns}
                            groups={groups}
                            dataSource={dataSource}
                            onEditComplete={this.onEditComplete}
                            onCellClick={this.onConceptCellClick}
                            renderColumnContextMenu={menuProps =>
                              menu(menuProps)}
                            resizable
                            editable={
                              campusAbility &&
                              campusAbility.can("save", "scoresV2")
                            }
                            sortable={false}
                            enableSelection={false}
                            rowHeight={50}
                            headerHeight={64}
                            style={
                              isFullscreen
                                ? { height: "100%", minHeight: 0 }
                                : { minHeight: 550 }
                            }
                            scrollProps={getDataGridScrollProps()}
                            i18n={getDataGridI18n(t)}
                            multiSelect={false}
                            cellSelection={cellSelection}
                            onCellSelectionChange={e => this.scrollTo(e)}
                            showColumnMenuToolOnHover={false}
                            enableColumnAutosize
                          />
                        )}
                      </GridFullscreen>

                      <Footer item xs={12}>
                        {formula && (
                          <HelpButton
                            onClick={() => this.setState({ openFormula: true })}
                          >
                            <HelpIcon />
                            <p>{t("grades.v2.avgCalc")}</p>
                          </HelpButton>
                        )}
                        <Can I="save" a="scoresV2">
                          {patternSnapshot && (
                            <PatternButton
                              variant="outlined"
                              color="primary"
                              startIcon={<Undo />}
                              onClick={() =>
                                this.setState({ openRevertPattern: true })}
                              style={{ marginRight: 16 }}
                            >
                              {t("grades.v2.revertDefault")}
                            </PatternButton>
                          )}
                          <PatternButton
                            variant="outlined"
                            color="primary"
                            startIcon={<PlaylistAddCheck />}
                            onClick={this.openApplyPatternDialog}
                          >
                            {t("grades.v2.applyDefault")}
                          </PatternButton>
                          <SaveButton
                            variant="contained"
                            color="primary"
                            disabled={!hasGradeChanges}
                            onClick={this.saveGrades}
                          >
                            {t("common.actions.saveChanges")}
                          </SaveButton>
                        </Can>
                      </Footer>
                    </>
                  )}
                </>
              )}
            </Grid>
          )}
        </Grid>

        {openTestDialog &&
          (!isEditing || (!loadingSaveGrade && evaluation)) && (
            <TestDialog
              open={openTestDialog}
              testTypes={testTypes}
              complementaryFields={complementaryFields}
              onSave={this.saveTest}
              onDelete={this.handleOnDeleteEvaluation}
              onClose={() => this.setState({ openTestDialog: false })}
              isEditing={isEditing}
              evaluation={evaluation}
            />
          )}

        <Dialog
          open={openConfirmTest}
          size="sm"
          fullWidth
          onClose={() => this.setState({ openConfirmTest: false })}
        >
          {isEditing ? (
            <DialogTitle>
              {loadingSaveTest
                ? t("grades.v2.changingEval")
                : t("grades.v2.evalChangedShort")}
            </DialogTitle>
          ) : (
            <DialogTitle>
              {loadingSaveTest
                ? t("grades.v2.savingEvals")
                : t("grades.v2.newEval")}
            </DialogTitle>
          )}
          {!loadingSaveTest ? (
            <>
              <DialogContent>
                <Heading>
                  {isEditing
                    ? t("grades.v2.evalCreatedEdited")
                    : t("grades.v2.evalCreated")}
                </Heading>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => this.setState({ openConfirmTest: false })}
                >
                  {t("common.actions.ok")}
                </Button>
              </DialogActions>
            </>
          ) : (
            <LoadContainer>
              <CircularProgress />
            </LoadContainer>
          )}
        </Dialog>

        <SimpleDialog
          open={loadingSaveGrade}
          onClose={() => this.setState({ loadingSaveGrade: false })}
          title={isEditing ? t("grades.v2.editEval") : t("grades.v2.saving")}
          loading
        />

        <SimpleDialog
          open={openFormula}
          closeBtn
          onClose={() => this.setState({ openFormula: false })}
          title={t("grades.v2.formula")}
          message={formula}
        />

        <Dialog
          open={openApplyPattern}
          size="sm"
          fullWidth
          onClose={() => this.setState({ openApplyPattern: false })}
        >
          <DialogTitle>{t("grades.v2.applyDefault")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              {t("grades.v2.defaultHelp")}
            </Typography>
            {patternHasConcepts && (
              <FormControl
                fullWidth
                variant="outlined"
                style={{ marginBottom: 16 }}
              >
                <InputLabel id="pattern-concept-label">
                  {t("grades.v2.defaultConcept")}
                </InputLabel>
                <Select
                  labelId="pattern-concept-label"
                  label={t("grades.v2.defaultConcept")}
                  value={patternConcept}
                  onChange={e =>
                    this.setState({ patternConcept: e.target.value })}
                >
                  {patternConceptOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {patternHasNumeric && (
              <TextField
                fullWidth
                variant="outlined"
                label={t("grades.v2.defaultGrade")}
                value={patternNumeric}
                onChange={e =>
                  this.setState({ patternNumeric: e.target.value })}
                inputProps={{ inputMode: "decimal" }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ openApplyPattern: false })}>
              {t("common.actions.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={this.applyPattern}
            >
              {t("grades.v2.apply")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openRevertPattern}
          size="sm"
          fullWidth
          onClose={() => this.setState({ openRevertPattern: false })}
        >
          <DialogTitle>{t("grades.v2.revertDefault")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {t("grades.v2.revertDefaultConfirm")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ openRevertPattern: false })}>
              {t("common.actions.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={this.revertPattern}
            >
              {t("grades.v2.revert")}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

ClassGradesV2.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired,
  jobsState: PropTypes.arrayOf(PropTypes.shape()).isRequired, // injected by react-redux mapStateToProps connect()
  location: PropTypes.shape({
    search: PropTypes.string,
    state: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
    )
  }).isRequired,
  t: PropTypes.func.isRequired
};

ClassGradesV2.contextType = AbilityContext;

const mapStateToProps = state => ({
  jobsState: state.jobsState
});

export default connect(mapStateToProps)(
  withTranslation()(withAlert()(withRouter(ClassGradesV2)))
);
